/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The icon set.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * Icons used to be emoji: ✏️ 👥 ✓ ✕ on meal cards, 👑 🙂 on Family, ☾ ☀ in the
 * topbar. Emoji are a bad icon system for three reasons that all showed up here:
 * they render as a different picture on every OS, they cannot be recoloured to
 * carry state (an emoji tick is always green, even when the thing it marks is
 * disabled), and they inherit the text metrics rather than a tap target, which
 * is how the meal-card buttons ended up at ~16px against a 44px minimum.
 *
 * These are stroke icons on a 24-grid, drawn with `currentColor`, so a single
 * `text-danger` on the parent recolours the icon and the label together.
 *
 * `aria-hidden` is the default and is correct: every caller in this app pairs
 * an icon with either visible text or an aria-label, so announcing the icon
 * too would just make screen readers say everything twice.
 */

export type IconName =
  | "home" | "calendar" | "book" | "cart" | "users" | "chat" | "settings"
  | "plus" | "check" | "circle" | "x" | "pencil" | "more" | "chevronDown"
  | "chevronLeft" | "chevronRight" | "star" | "starFilled" | "archive"
  | "copy" | "printer" | "calendarPlus" | "sparkles" | "camera" | "link"
  | "pen" | "sun" | "moon" | "logout" | "crown" | "person" | "clock"
  | "alert" | "search" | "croissant" | "send" | "trash" | "download";

/** 24×24 path data. Stroke unless the name says otherwise. */
const PATHS: Record<IconName, string> = {
  home: "M3 10.5 12 3l9 7.5M5.5 9.5V20a1 1 0 0 0 1 1H10v-6h4v6h3.5a1 1 0 0 0 1-1V9.5",
  calendar: "M4 6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 19.5zM4 10h16M8.5 3v4M15.5 3v4",
  book: "M4 5.5A1.5 1.5 0 0 1 5.5 4H19v14.5H5.5A1.5 1.5 0 0 0 4 20zM4 5.5V20M19 18.5V21H5.5",
  cart: "M3 4h2l2.4 10.5a1.5 1.5 0 0 0 1.5 1.2h7.9a1.5 1.5 0 0 0 1.5-1.2L20 7H6M9 20a1 1 0 1 0 0-.01M17 20a1 1 0 1 0 0-.01",
  users: "M15.5 20v-1.5a3.5 3.5 0 0 0-3.5-3.5H6a3.5 3.5 0 0 0-3.5 3.5V20M12 7.5A3.5 3.5 0 1 1 5 7.5a3.5 3.5 0 0 1 7 0M17 15.2a3.5 3.5 0 0 1 4.5 3.3V20M16.5 4.2a3.5 3.5 0 0 1 0 6.6",
  chat: "M20 14.5a2 2 0 0 1-2 2H8l-4 3.5v-14a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z",
  settings: "M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4M19.1 14.4a1.5 1.5 0 0 0 .3 1.7l.1.1a1.8 1.8 0 1 1-2.6 2.6l-.1-.1a1.5 1.5 0 0 0-2.5 1v.2a1.8 1.8 0 1 1-3.6 0v-.1a1.5 1.5 0 0 0-2.6-1l-.1.1a1.8 1.8 0 1 1-2.6-2.6l.1-.1a1.5 1.5 0 0 0-1-2.5H4.4a1.8 1.8 0 1 1 0-3.6h.1a1.5 1.5 0 0 0 1-2.6l-.1-.1a1.8 1.8 0 1 1 2.6-2.6l.1.1a1.5 1.5 0 0 0 1.7.3h.1a1.5 1.5 0 0 0 .9-1.4V4.4a1.8 1.8 0 1 1 3.6 0v.1a1.5 1.5 0 0 0 2.5 1l.1-.1a1.8 1.8 0 1 1 2.6 2.6l-.1.1a1.5 1.5 0 0 0-.3 1.7v.1a1.5 1.5 0 0 0 1.4.9h.2a1.8 1.8 0 1 1 0 3.6h-.1a1.5 1.5 0 0 0-1.4.9z",
  plus: "M12 5v14M5 12h14",
  check: "M20 6 9 17l-5-5",
  circle: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18",
  x: "M18 6 6 18M6 6l12 12",
  pencil: "M17 3.5a2.1 2.1 0 0 1 3 3L7.5 19 3 20.5 4.5 16z",
  more: "M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2M19 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2M5 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2",
  chevronDown: "m6 9 6 6 6-6",
  chevronLeft: "m15 18-6-6 6-6",
  chevronRight: "m9 18 6-6-6-6",
  star: "m12 3.5 2.7 5.6 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1L3.2 10l6.1-.9z",
  starFilled: "m12 3.5 2.7 5.6 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1L3.2 10l6.1-.9z",
  archive: "M3 7h18v3H3zM5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9M10 14h4",
  copy: "M9 9h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V10a1 1 0 0 1 1-1M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1",
  printer: "M7 8V3h10v5M7 18H5a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-2M7 14h10v7H7z",
  calendarPlus: "M4 6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 19.5zM4 10h16M8.5 3v4M15.5 3v4M12 13v5M9.5 15.5h5",
  sparkles: "m12 3 1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9zM18.5 15l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z",
  camera: "M20 18a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18V9a1.5 1.5 0 0 1 1.5-1.5h2.2l1.3-2.2h5.9l1.3 2.2h2.3A1.5 1.5 0 0 1 20 9zM12 16.2a3.6 3.6 0 1 0 0-7.2 3.6 3.6 0 0 0 0 7.2",
  link: "M10.5 13.5a3.5 3.5 0 0 0 5 0l3-3a3.54 3.54 0 0 0-5-5l-1.7 1.7M13.5 10.5a3.5 3.5 0 0 0-5 0l-3 3a3.54 3.54 0 0 0 5 5l1.7-1.7",
  pen: "M11 4H5a1.5 1.5 0 0 0-1.5 1.5v13A1.5 1.5 0 0 0 5 20h13a1.5 1.5 0 0 0 1.5-1.5v-6M17.5 3.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z",
  sun: "M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4",
  moon: "M20.5 14.3A8.5 8.5 0 1 1 9.7 3.5a6.6 6.6 0 0 0 10.8 10.8",
  logout: "M9 21H5.5A1.5 1.5 0 0 1 4 19.5v-15A1.5 1.5 0 0 1 5.5 3H9M16 16l4-4-4-4M20 12H9",
  crown: "M4 18h16M4 18 3 7l5 4 4-6 4 6 5-4-1 11",
  person: "M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8",
  clock: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M12 7v5l3 2",
  alert: "M12 9v4M12 17h.01M10.3 3.9 2.4 17.3a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0",
  search: "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.3-4.3",
  // Monsieur Croissant's mark. Drawn on the same 24-grid as everything else
  // rather than shipped as an image, so it inherits currentColor and recolours
  // with the button it sits in — a PNG would have needed a dark-mode twin.
  // Dome, two horn tips, the scalloped underside, and three fold lines.
  croissant:
    "M3.2 15.8c0-4.6 3.9-8.3 8.8-8.3s8.8 3.7 8.8 8.3c0 1-1 1.7-1.9 1.4l-2.2-.7" +
    "-2.3 1.1c-.9.4-1.9.4-2.8 0l-2.3-1.1-2.2.7c-.9.3-1.9-.4-1.9-1.4Z" +
    "M3.2 15.8 1.4 18.6M20.8 15.8l1.8 2.8" +
    "M8.4 8.6 7.1 16.9M12 7.5v9.8M15.6 8.6l1.3 8.3",
  send: "M4 12 20 4l-8 16-2-6z M10 14l10-10",
  trash: "M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13M10 11v6M14 11v6",
  download: "M12 4v11M8 11.5l4 4 4-4M4 19h16",
};

/** Names drawn filled rather than stroked. */
const FILLED = new Set<IconName>(["starFilled"]);

export function Icon({ name, className = "h-4 w-4", label }: {
  name: IconName;
  className?: string;
  /** Supply ONLY when the icon is the sole content of its control. */
  label?: string;
}) {
  const filled = FILLED.has(name);
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={filled ? 0 : 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

/**
 * An icon-only button with a guaranteed 44×44 hit area.
 *
 * The visible glyph stays small; the target does not. This is the component
 * that fixes the meal-card action buttons, which had no padding at all and
 * were roughly 16px square inside an already-narrow grid column.
 */
export function IconButton({
  name, label, onClick, tone = "default", className = "", disabled, title,
}: {
  name: IconName;
  /** Required: an icon-only control with no accessible name is unusable. */
  label: string;
  onClick?: (e: React.MouseEvent) => void;
  tone?: "default" | "accent" | "danger";
  className?: string;
  disabled?: boolean;
  title?: string;
}) {
  const toneCls =
    tone === "danger" ? "text-ink-3 hover:text-danger hover:bg-danger/10"
    : tone === "accent" ? "text-accent hover:bg-accent/10"
    : "text-ink-3 hover:text-ink hover:bg-paper-3";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={title ?? label}
      className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg
                  transition disabled:opacity-40 ${toneCls} ${className}`}
    >
      <Icon name={name} className="h-[18px] w-[18px]" />
    </button>
  );
}
